// Profile API client and switcher state.
//
// Kept free of DOM work so it can be unit-tested; the switcher's rendering
// lives in profile-bar.ts (Task 17).

const API_BASE = '/api';

export interface Profile {
  name: string;
  display_name: string;
  base: string;
  forked: boolean;
  behind_by: number;
  slot: number;
  port: number;
  desired_running: boolean;
  state: 'running' | 'stopped' | 'failed' | 'unknown';
  last_error?: string;
}

export interface CreateProfileRequest {
  name: string;
  display_name?: string;
  base: 'wizard' | 'player' | 'bare';
  mode: 'inherit' | 'fork';
  character?: string;
  host?: string;
  port?: number;
}

async function request<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(init?.headers ?? {}),
    },
  });
  if (!res.ok) {
    const text = (await res.text()).trim();
    throw new Error(text || `Request failed with status ${res.status}`);
  }
  return (await res.json()) as T;
}

export async function listProfiles(token: string): Promise<Profile[]> {
  // The server currently always returns a JSON array (ListProfiles builds it with
  // make([]profileJSON, 0, len(rows))), but Go marshals a nil slice as null, and
  // that's one `var out []profileJSON` refactor away from happening here. Guard
  // against that regression so no caller has to.
  return (await request<Profile[] | null>(token, `${API_BASE}/profiles`)) ?? [];
}

export async function createProfile(token: string, req: CreateProfileRequest): Promise<void> {
  await request(token, `${API_BASE}/profiles`, {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function deleteProfile(token: string, name: string): Promise<void> {
  await request(token, `${API_BASE}/profiles/${encodeURIComponent(name)}`, {
    method: 'DELETE',
  });
}

export async function startProfile(token: string, name: string): Promise<void> {
  await request(token, `${API_BASE}/profiles/${encodeURIComponent(name)}/start`, {
    method: 'POST',
  });
}

export async function stopProfile(token: string, name: string): Promise<void> {
  await request(token, `${API_BASE}/profiles/${encodeURIComponent(name)}/stop`, {
    method: 'POST',
  });
}

export async function forkProfile(token: string, name: string): Promise<void> {
  await request(token, `${API_BASE}/profiles/${encodeURIComponent(name)}/fork`, {
    method: 'POST',
  });
}

export async function renameProfile(token: string, name: string, displayName: string): Promise<void> {
  await request(token, `${API_BASE}/profiles/${encodeURIComponent(name)}`, {
    method: 'PATCH',
    body: JSON.stringify({ display_name: displayName }),
  });
}

/**
 * Pick the profile to attach to on load: the one the user last looked at if it
 * still exists, else the first running one, else the first one at all.
 */
export function selectInitialProfile(profiles: Profile[], remembered: string | null): Profile | null {
  if (profiles.length === 0) return null;
  if (remembered) {
    const match = profiles.find((p) => p.name === remembered);
    if (match) return match;
  }
  return profiles.find((p) => p.state === 'running') ?? profiles[0];
}

const REMEMBER_PREFIX = 'lera.profile.';

export function rememberProfile(userId: string, name: string): void {
  try {
    localStorage.setItem(REMEMBER_PREFIX + userId, name);
  } catch {
    // Private browsing and blocked site data both throw; the selection simply
    // is not remembered.
  }
}

export function recallProfile(userId: string): string | null {
  try {
    return localStorage.getItem(REMEMBER_PREFIX + userId);
  } catch {
    return null;
  }
}

/**
 * Clear the remembered profile once the name it refers to no longer exists
 * (deleted out from under the user). Without this, a later reconnect could
 * recall a slug that a brand-new profile has since reclaimed a port for.
 */
export function forgetProfile(userId: string): void {
  try {
    localStorage.removeItem(REMEMBER_PREFIX + userId);
  } catch {
    // Same private-browsing / blocked-site-data exception as rememberProfile.
  }
}

export function profileStatusLabel(p: Profile): string {
  switch (p.state) {
    case 'running':
      return 'Running';
    case 'stopped':
      return 'Stopped';
    case 'failed':
      return 'Failed';
    default:
      return 'Starting...';
  }
}
